import React from 'react';
import styled from 'styled-components';
import { system, extractSysProps } from './system';

// From molekule Avatar — default colors from flatuicolors.com
const DEFAULT_COLORS = [
  '#2ecc71', // emerald
  '#3498db', // peter river
  '#8e44ad', // wisteria
  '#e67e22', // carrot
  '#e74c3c', // alizarin
  '#1abc9c', // turquoise
  '#2c3e50', // midnight blue
];

function getInitials(name: string = ''): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('');
}

// System prop keys consumed by Avatar (not forwarded to DOM).
// Intentionally narrower than the full SYS_KEYS_SET — molekule's Avatar
// composed fewer utilities than Box.
const AVATAR_SYS_KEYS: ReadonlySet<string> = new Set([
  'm',
  'mt',
  'mb',
  'ml',
  'mr',
  'mx',
  'my',
  'p',
  'pt',
  'pb',
  'pl',
  'pr',
  'px',
  'py',
  'display',
  'alignSelf',
  'cursor',
  'opacity',
]);

interface StyledAvatarProps {
  $size: number;
  $borderRadius: string | number;
  $backgroundColor: string;
  $color: string;
  $src?: string;
  $sys?: Record<string, any>;
}

const StyledAvatar = styled.div<StyledAvatarProps>`
  height: ${(p) => p.$size}px;
  width: ${(p) => p.$size}px;
  border-radius: ${(p) => p.$borderRadius};
  background-color: ${(p) => p.$backgroundColor};
  color: ${(p) => p.$color};
  text-align: center;
  line-height: ${(p) => p.$size}px;
  font-size: ${(p) => Math.round(p.$size * 0.5)}px;
  ${(p) =>
    p.$src &&
    `
    background-image: url(${p.$src});
    background-size: cover;
    background-repeat: no-repeat;
  `}
  ${(p) => system(p.$sys, p.theme)}
`;

export interface AvatarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  name?: string;
  src?: string;
  size?: number;
  borderRadius?: string | number;
  color?: string;
  backgroundColor?: string;
  [key: string]: any;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  function Avatar(props, ref) {
    const {
      name = '',
      src,
      size = 25,
      borderRadius = '100%',
      color = 'white',
      backgroundColor,
      ...remaining
    } = props;

    const { sys, rest } = extractSysProps(remaining as Record<string, any>, AVATAR_SYS_KEYS);

    // Resolve background color: if a theme color key, resolve from theme; else use as-is
    // The actual resolution is handled in StyledAvatar via $backgroundColor directly
    // since we don't have direct theme access here. The call site passes Theme.colors.secondaryLight
    // directly as a hex value, so this is fine.
    const resolvedBg =
      backgroundColor || DEFAULT_COLORS[name.length % DEFAULT_COLORS.length];

    return (
      <StyledAvatar
        ref={ref}
        $size={size}
        $borderRadius={borderRadius}
        $backgroundColor={resolvedBg}
        $color={color}
        $src={src}
        $sys={sys}
        aria-label={name}
        {...rest}
      >
        {src ? null : getInitials(name)}
      </StyledAvatar>
    );
  },
);

export default Avatar;
